import { ReactNode, useRef, useEffect, useState } from 'react';
import { OrganizationDetailsContext } from '@vivli/features/organizations/infrastructure/context';
import {
    IOrganization,
    IOrganizationInvitation,
    IOrganizationMember,
    IOrganizationUpdate
} from '@vivli/features/organizations/infrastructure/interface';
import { first } from 'rxjs/operators';
import { LoadIndicatorCenteredView } from '@vivli/shared/views';
import { useNavigate, useParams } from 'react-router';
import { useCurrentMembersHook } from '@vivli/features/organizations/infrastructure/hook';
import { IUser } from '@vivli/shared/infrastructure/interface';
import {
    useAssignedAppTypeContext,
    useModalApiContext,
    useOrganizationsApiContext,
    useToastApiContext
} from '@vivli/core/infrastructure/context';
import { forkJoin } from 'rxjs';
import { AddMemberComponent } from '@vivli/features/organizations/components';
import { GridApi } from 'ag-grid-community';

interface OrganizationDetailsContextWrapperProps {
    children: ReactNode;
}

export const OrganizationDetailsContextWrapper = ({ children }: OrganizationDetailsContextWrapperProps) => {
    const { orgId } = useParams();
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const orgsService = useOrganizationsApiContext();
    const { isAmr } = useAssignedAppTypeContext();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState<IOrganization>();
    const [isLoading, setIsLoading] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const { currentMembers, setCurrentMembers, getCurrentMembers, amendCurrentMembers, rolesOrgSpecific } = useCurrentMembersHook(orgId);
    const [updatedUser, setUpdatedUser] = useState<IOrganizationMember>();
    const [isMemberTab, setIsMemberTab] = useState<boolean>(false);
    const membersGridApiRef = useRef<GridApi>();

    const handleError = (e, toast?: string) => {
        setIsLoading(false);
        setIsSaving(false);
        const message = toast ? toast + e : e;
        modalService.error(message);
    };

    const getOrganization = (orgId: string) => {
        setIsLoading(true);
        orgsService
            .getOrganization(orgId)
            .pipe(first())
            .subscribe(
                (org) => {
                    setOrganization(org);
                    setIsLoading(false);
                },
                (e) => {
                    handleError(e, `There was a problem getting Organization : ${orgId} -${e} `);
                }
            );
    };

    const addOrgMember = (orgInvite: IOrganizationInvitation) => {
        setIsSaving(true);
        orgsService
            .inviteOrganizationMember(organization.id, orgInvite, isAmr)
            .pipe(first())
            .subscribe(() => {
                toastService.success(`${orgInvite.email} successfully added.`);
                /* obsolete
                const newCount = invitationsCount + 1;

                setInvitationsCount(newCount);  */
                setIsSaving(false);
                getCurrentMembers();
            }, handleError);
    };

    const handleSave = (updatedOrg: IOrganization) => {
        setIsSaving(true);
        if (updatedOrg.id) {
            handleUpdateOrg(updatedOrg);
        } else {
            handleAddOrg(updatedOrg);
        }
    };

    function handleOrganizationUpdate(savedOrg: IOrganizationUpdate, updatedOrg: IOrganization) {
        if (savedOrg.returnValue === 'ok') {
            toastService.success('Organization saved successfully.');
            setIsSaving(false);
            setOrganization(savedOrg.orgToUpdate);
        }
        if (savedOrg.returnValue === 'checkDomain') {
            handleDomainWarningOnUpdate(updatedOrg);
        }
    }

    const handleUpdateOrg = (updatedOrg: IOrganization) => {
        const membersToUpdate = currentMembers?.filter((mem) => mem.wasUpdated);
        const anyMembershipUpdates = membersToUpdate && membersToUpdate.length > 0;
        if (anyMembershipUpdates) {
            const saveOrgCall = orgsService.updateOrganization(updatedOrg, isAmr);
            const saveMembership = orgsService.updateOrganizationMembers(membersToUpdate, updatedOrg.id);
            forkJoin([saveOrgCall, saveMembership]).subscribe({
                next: ([savedOrg, updatedMembers]) => {
                    amendCurrentMembers(updatedMembers);
                    handleOrganizationUpdate(savedOrg, updatedOrg);
                },
                error: (e) => {
                    handleError(e, 'There was a problem saving this Organization: ');
                }
            });
        } else {
            orgsService
                .updateOrganization(updatedOrg, isAmr)
                .pipe(first())
                .subscribe(
                    (savedOrg) => {
                        handleOrganizationUpdate(savedOrg, updatedOrg);
                    },
                    (e) => {
                        handleError(e, 'There was a problem saving this Organization: ');
                    }
                );
        }
    };

    const handleAddOrg = (updatedOrg: IOrganization) => {
        orgsService
            .addOrganization(updatedOrg, isAmr)
            .pipe(first())
            .subscribe(
                (newOrg) => {
                    if (newOrg.returnValue === 'ok') {
                        toastService.success(
                            'Organization created successfully.  You may now make additional changes to ' + 'your newly created org.'
                        );
                        setIsSaving(false);
                        navigate(`/admin/orgs/${newOrg.newOrgId}`);
                    }
                    if (newOrg.returnValue === 'checkDomain') {
                        handleDomainWarningOnAdd(updatedOrg);
                        setIsSaving(false);
                    }
                },
                (e) => {
                    handleError(e, 'There was a problem saving this Organization: ');
                }
            );
    };
    const handleDomainWarningOnAdd = (updatedOrg) => {
        modalService.confirm(`Another organization is already using a domain with the same name.  Click Yes to save anyway.`, {
            title: 'Duplicate Domain',
            onConfirm: () => {
                orgsService
                    .addForSharedDomainOrganization(updatedOrg, isAmr)
                    .pipe(first())
                    .subscribe(
                        (newOrg) => {
                            toastService.success(
                                'Organization created successfully. You may now make additional changes to ' + 'your newly created org.'
                            );
                            setIsSaving(false);
                            navigate(`/admin/orgs/${newOrg.newOrgId}`);
                        },
                        (e) => {
                            handleError(e, 'There was a problem saving this Organization: ');
                        }
                    );
            }
        });
    };

    const handleDomainWarningOnUpdate = (updatedOrg) => {
        modalService.confirm(`Another organization is already using a domain with the same name.  Click Yes to save anyway.`, {
            title: 'Duplicate Domain',
            onConfirm: () => {
                orgsService
                    .updateForSharedDomainOrganization(updatedOrg, isAmr)
                    .pipe(first())
                    .subscribe(
                        (newOrg) => {
                            toastService.success('Organization updated successfully.');
                            setIsSaving(false);
                            setOrganization(newOrg.orgToUpdate);
                        },
                        (e) => {
                            handleError(e, 'There was a problem saving this Organization: ');
                        }
                    );
            },
            onCancel: () => setIsSaving(false)
        });
    };

    const showAddMemberPopup = () => {
        modalService.message(<AddMemberComponent organization={organization} addOrgMember={addOrgMember} isSaving={isSaving} />, {
            showLogo: true,
            showBackground: false,
            style: { backgroundColor: 'rgb(233, 234, 239)' }
        });
    };

    const handleRoleUpdate = (
        orgId: string,
        fieldName: string,
        userToUpdate: IUser,
        setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        const newCurrentMembers = [...currentMembers];
        if (newCurrentMembers.length <= 0) return;
        const index = newCurrentMembers.findIndex((m) => m.id === userToUpdate.id);
        if (index < 0) return;
        const storedOrgMembership = newCurrentMembers[index]; //.find((m) => m.id === userToUpdate.id);
        const currentRoleValue = storedOrgMembership[fieldName];
        storedOrgMembership[fieldName] = !currentRoleValue;
        const membership = storedOrgMembership.userRole.orgMemberships.find((m) => m.orgId === orgId);

        let updated = false;

        if (fieldName.includes('hasRole')) {
            const realName = fieldName.replace('hasRole', '');

            if (!currentRoleValue) {
                //role should be present
                if (membership && !membership.vivliRoles.find((r) => r.code === realName)) {
                    const roleDef = rolesOrgSpecific.find((r) => r.code === realName);

                    if (roleDef) {
                        membership.vivliRoles.push(roleDef);
                        updated = true;
                    }
                }
            } else {
                //role should be removed
                const role = membership?.vivliRoles.find((r) => r.code === realName);

                if (role) {
                    membership.vivliRoles = membership.vivliRoles.filter((r) => r.code !== realName);
                    updated = true;
                }
            }
        } else {
            //just a field
            membership[fieldName] = !currentRoleValue;
            updated = true;
        }
        if (updated) {
            storedOrgMembership.wasUpdated = true;
            setUpdatedUser(storedOrgMembership);
            setCurrentMembers(newCurrentMembers);
            setTimeout(() => membersGridApiRef.current.ensureIndexVisible(index, 'middle'), 100);
        }
    };

    //one Save serves the Details, Members and ROR tabs, and member role edits never reach the form -
    //handleRoleUpdate flags the row instead, which is what handleUpdateOrg reads to decide whether to
    //save memberships. So the form's dirty state alone would leave Save dead over a pending role change.
    const hasMemberChanges = !!currentMembers?.some((member) => member.wasUpdated);

    const provider = {
        organization,
        handleSave,
        isSaving,
        hasMemberChanges,
        addOrgMember,
        currentMembers,
        setCurrentMembers,
        handleRoleUpdate,
        updatedUser,
        isLoading,
        rolesOrgSpecific,
        isMemberTab,
        setIsMemberTab,
        showAddMemberPopup,
        membersGridApiRef
    };

    useEffect(() => {
        if (orgId && orgId !== 'add') {
            getOrganization(orgId);
        }
    }, [orgId]);

    if (isLoading) {
        return <LoadIndicatorCenteredView />;
    }

    return <OrganizationDetailsContext.Provider value={provider}>{children}</OrganizationDetailsContext.Provider>;
};
